import React, { useState, useEffect, useCallback, useRef } from "react";
import { Table, Thead, Tbody, Tr, Th, Td } from "react-super-responsive-table";
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css";
import clientConfig from "../config";
import { getAuthHeaders } from "../utils/apiFetch";
import styles from "./AccidentReportTable.module.css";
import { MoonLoader } from "react-spinners";
import DeleteIcon from "../components/icons/DeleteIcon";
import useIsInspection from "../hooks/useIsInspection";
import Button from "../components/UiElements/Button";
import Dropdown, { type DropdownItem } from "../components/UiElements/Dropdown";
import Checkbox from "../components/UiElements/Checkbox";
import ErrorMessage from "../components/UiElements/ErrorMessage";
import { tokens } from "../design-system/tokens";
import { useDraggingScroll } from "../hooks/useDraggingScroll";

interface CarInspection {
  _id: string;
  time: string;
  date: string;
  carNumber: string;
  pakaNumber: string;
  fuelLevel: string;
  km: string;
  notes: string;
  inspectorName: string;
  requesterName: string;
  createdAt: string;
}

const translateFuelLevel = (fuelLevel: string): string => {
  const translations: Record<string, string> = {
    empty: "ריק",
    quarter: "רבע",
    half: "חצי",
    "three-quarters": "שלושה רבעים",
    full: "מלא",
  };
  return translations[fuelLevel] || fuelLevel;
};

const CarInspectionTable = () => {
  const isInspection = useIsInspection();
  const [inspections, setInspections] = useState<CarInspection[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    show: boolean;
    ids: string[];
    carNumber?: string;
    date?: string;
    isBulk: boolean;
  }>({
    show: false,
    ids: [],
    carNumber: "",
    date: "",
    isBulk: false,
  });
  const [selectedInspections, setSelectedInspections] = useState<string[]>([]);
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(
    null
  );
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const tableScrollProps = useDraggingScroll();

  const loadInspections = useCallback(
    async (pageNum: number = 1, append: boolean = false) => {
      setLoading(true);
      setError(null);
      try {
        const apiUrl = `${clientConfig.backendUrl}/forms/car-inspection`;
        const params = new URLSearchParams({
          page: String(pageNum),
          limit: "100",
        });
        const response = await fetch(`${apiUrl}?${params}`, { headers: getAuthHeaders() });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            (errorData as { error?: string }).error ||
              `HTTP error! status: ${response.status}`
          );
        }

        const data = await response.json();
        const newInspections: CarInspection[] = data.inspections || [];
        const totalPages: number = data.totalPages ?? 1;

        if (append) {
          setInspections((prev) => [...prev, ...newInspections]);
        } else {
          setInspections(newInspections);
        }

        setHasMore(pageNum < totalPages);
        setPage(pageNum);
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : "שגיאה בטעינת הנתונים";
        setError(message);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const deleteInspections = useCallback(async (ids: string[]) => {
    try {
      setBulkDeleting(true);
      await Promise.all(
        ids.map(async (id) => {
          const response = await fetch(
            `${clientConfig.backendUrl}/forms/car-inspection/${id}`,
            { method: "DELETE", headers: getAuthHeaders() }
          );
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
        })
      );
      setInspections((prev) => prev.filter((item) => !ids.includes(item._id)));
      setSelectedInspections((prev) => prev.filter((id) => !ids.includes(id)));
      setDeleteConfirm({
        show: false,
        ids: [],
        carNumber: "",
        date: "",
        isBulk: false,
      });
    } catch {
      alert("שגיאה במחיקה. נסה שוב.");
    } finally {
      setBulkDeleting(false);
    }
  }, []);

  const handleLoadMore = useCallback(() => {
    if (!loading && hasMore) {
      loadInspections(page + 1, true);
    }
  }, [loading, hasMore, page, loadInspections]);

  const showDeleteConfirm = (id: string, carNumber: string, date: string) => {
    setDeleteConfirm({
      show: true,
      ids: [id],
      carNumber,
      date,
      isBulk: false,
    });
  };

  const cancelDelete = () => {
    setDeleteConfirm({
      show: false,
      ids: [],
      carNumber: "",
      date: "",
      isBulk: false,
    });
  };

  const confirmDelete = () => {
    if (deleteConfirm.ids.length) {
      deleteInspections(deleteConfirm.ids);
    }
  };

  const toggleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedInspections(inspections.map((inspection) => inspection._id));
    } else {
      setSelectedInspections([]);
    }
    setLastSelectedIndex(null);
  };

  const toggleSelectInspection = (
    inspection: CarInspection,
    index: number,
    shiftKey: boolean
  ) => {
    setSelectedInspections((prev) => {
      let updated = [...prev];
      const isSelected = updated.includes(inspection._id);
      if (shiftKey && lastSelectedIndex !== null) {
        const [start, end] = [
          Math.min(lastSelectedIndex, index),
          Math.max(lastSelectedIndex, index),
        ];
        const rangeIds = inspections
          .slice(start, end + 1)
          .map((item) => item._id);
        if (isSelected) {
          updated = updated.filter((id) => !rangeIds.includes(id));
        } else {
          rangeIds.forEach((id) => {
            if (!updated.includes(id)) updated.push(id);
          });
        }
      } else if (isSelected) {
        updated = updated.filter((id) => id !== inspection._id);
      } else {
        updated.push(inspection._id);
      }
      return updated;
    });
    setLastSelectedIndex(index);
  };

  const handleDeleteSelected = () => {
    if (!selectedInspections.length) return;
    setDeleteConfirm({
      show: true,
      ids: selectedInspections,
      isBulk: true,
      carNumber: "",
      date: "",
    });
  };

  const handleDownloadExcel = async () => {
    try {
      setDownloading(true);
      const response = await fetch(
        `${clientConfig.backendUrl}/forms/car-inspection/export-excel`,
        { headers: getAuthHeaders() }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const contentDisposition = response.headers.get("content-disposition");
      let fileName = `ביקורות_רכב_${
        new Date().toISOString().split("T")[0]
      }.xlsx`;
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(
          /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
        );
        if (fileNameMatch?.[1]) {
          fileName = decodeURIComponent(fileNameMatch[1].replace(/['"]/g, ""));
        }
      }

      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      alert("שגיאה בהורדת הקובץ. נסה שוב.");
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    loadInspections(1, false);
  }, [loadInspections]);

  useEffect(() => {
    setSelectedInspections((prev) =>
      prev.filter((id) =>
        inspections.some((inspection) => inspection._id === id)
      )
    );
  }, [inspections]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          handleLoadMore();
        }
      },
      { threshold: 0.1 }
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) observer.observe(currentRef);

    return () => {
      if (currentRef) observer.unobserve(currentRef);
      observer.disconnect();
    };
  }, [hasMore, loading, handleLoadMore]);

  const DeleteConfirmDialog = deleteConfirm.show ? (
    <div className={styles.ConfirmDelete}>
      <div className={styles.TitleDelete} style={tokens.typography.h6}>
        {deleteConfirm.isBulk
          ? `למחוק ${deleteConfirm.ids.length} ביקורות רכב שנבחרו?`
          : `למחוק ביקורת רכב מספר ${deleteConfirm.carNumber} מתאריך ${deleteConfirm.date}?`}
      </div>
      <div className={styles.BtContainer}>
        <Button
          label={bulkDeleting ? "מוחק..." : "מחק"}
          color="primary"
          size="medium"
          width={120}
          onClick={confirmDelete}
          disabled={bulkDeleting}
        />
        <Button
          label="ביטול"
          color="outlineSecondary"
          size="medium"
          width={120}
          onClick={cancelDelete}
        />
      </div>
    </div>
  ) : null;

  const EmptyState =
    !loading && inspections.length === 0 ? (
      <div className={styles.EmptyState}>אין ביקורות רכב להצגה</div>
    ) : null;

  const bulkDropdownItems: DropdownItem[] = [
    {
      id: "delete",
      label: bulkDeleting ? "מוחק..." : "מחק נבחרים",
      onClick: handleDeleteSelected,
      disabled: !selectedInspections.length || bulkDeleting || loading,
    },
  ];

  const colCount = isInspection ? 10 : 11;

  return (
    <div className={styles.Container} dir="rtl">
      <div className={styles.HeaderContainer}>
        <div className={styles.Title} style={tokens.typography.h2}>
          טבלת ביקורות רכב
        </div>
        <Button
          label={downloading ? "מוריד..." : "הורד קובץ Excel"}
          color="secondary"
          size="medium"
          width="auto"
          onClick={handleDownloadExcel}
          disabled={downloading || inspections.length === 0}
        />
      </div>
      {DeleteConfirmDialog}
      {loading && inspections.length === 0 ? (
        <div className={styles.LoadingContainer}>
          <MoonLoader size={50} color={tokens.colors.secondary} />
        </div>
      ) : error ? (
        <div className={styles.ErrorState}>
          <ErrorMessage message={error} size="medium" />
          <Button
            label="נסה שוב"
            color="secondary"
            size="medium"
            width={120}
            onClick={() => loadInspections(1, false)}
          />
        </div>
      ) : (
        <>
          {EmptyState}
          {inspections.length > 0 && (
            <div className={styles.TableWrapper}>
              <div className={styles.TableInner}>
                <div className={styles.BulkActions}>
                  {!isInspection && (
                    <div className={styles.BulkActionsGroup}>
                      <Dropdown
                        triggerLabel="פעולות"
                        triggerColor="outlineSecondary"
                        triggerSize="medium"
                        items={bulkDropdownItems}
                        ariaLabel="פעולות על נבחרים"
                      />
                      {selectedInspections.length ? (
                        <span className={styles.SelectedCount}>
                          {`${selectedInspections.length} נבחרו`}
                        </span>
                      ) : null}
                    </div>
                  )}
                  <div className={styles.LinkContainer}>
                    <a
                      href="https://rafael-car-show.co.il/car-inspection"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        ...tokens.typography.body,
                        color: tokens.colors.text.secondary,
                        textDecoration: "underline",
                      }}
                    >
                      לעמוד הטופס
                    </a>
                  </div>
                </div>
                <div className={styles.TableFrame}>
                  <div
                    ref={tableScrollProps.ref}
                    className={`${styles.TableScroll} ${tableScrollProps.isDraggingScroll ? styles.TableScrollDragging : ""}`}
                    onMouseDown={tableScrollProps.onMouseDown}
                    style={tableScrollProps.style}
                  >
                    <Table className={styles.Table}>
                      <Thead>
                        <Tr className={styles.TrHeader}>
                          <Th
                            className={`${styles.ThTitle} ${styles.SelectHeader}`}
                          >
                            <Checkbox
                              name="select-all-inspections"
                              checked={
                                inspections.length > 0 &&
                                selectedInspections.length ===
                                  inspections.length
                              }
                              semiSelected={
                                selectedInspections.length > 0 &&
                                selectedInspections.length <
                                  inspections.length
                              }
                              onChange={(e) =>
                                toggleSelectAll(e.target.checked)
                              }
                              size="small"
                              color="onPrimary"
                              disabled={isInspection}
                              className={`${styles.TableCheckbox} ${styles.hideOnMobile}`}
                              ariaLabel="בחר הכל"
                            />
                          </Th>
                          <Th className={styles.ThTitle}>שעה</Th>
                          <Th className={styles.ThTitle}>תאריך</Th>
                          <Th className={styles.ThTitle}>מספר רכב</Th>
                          <Th className={styles.ThTitle}>פק״ע</Th>
                          <Th className={styles.ThTitle}>מיכל דלק</Th>
                          <Th className={styles.ThTitle}>קילומטרים</Th>
                          <Th className={styles.ThTitle}>הערות</Th>
                          <Th className={styles.ThTitle}>שם הבודק</Th>
                          <Th className={styles.ThTitle}>שם המזמין</Th>
                          {!isInspection && (
                            <Th className={styles.ThTitle}>מחק</Th>
                          )}
                        </Tr>
                      </Thead>
                      <Tbody>
                        {inspections.map((inspection, index) => (
                          <Tr key={inspection._id} className={styles.TrData}>
                            <Td
                              className={`${styles.SelectCell} ${styles.SelectHeader}`}
                            >
                              <Checkbox
                                name={`select-inspection-${inspection._id}`}
                                checked={selectedInspections.includes(
                                  inspection._id
                                )}
                                onChange={(e) =>
                                  toggleSelectInspection(
                                    inspection,
                                    index,
                                    (e.nativeEvent as MouseEvent).shiftKey
                                  )
                                }
                                size="small"
                                disabled={isInspection}
                                className={styles.TableCheckbox}
                                ariaLabel={`בחר רכב ${inspection.carNumber}`}
                              />
                            </Td>
                            <Td
                              className={styles.TimeContainer}
                              data-label="שעה"
                            >
                              {inspection.time}
                            </Td>
                            <Td
                              className={styles.DateContainer}
                              data-label="תאריך"
                            >
                              {inspection.date}
                            </Td>
                            <Td
                              className={styles.CarNumberContainer}
                              data-label="מספר רכב"
                            >
                              {inspection.carNumber}
                            </Td>
                            <Td
                              className={styles.PakaNumberContainer}
                              data-label="פק״ע"
                            >
                              {inspection.pakaNumber}
                            </Td>
                            <Td
                              className={styles.FuelLevelContainer}
                              data-label="מיכל דלק"
                            >
                              {translateFuelLevel(inspection.fuelLevel)}
                            </Td>
                            <Td
                              className={styles.KmContainer}
                              data-label="קילומטרים"
                            >
                              {inspection.km}
                            </Td>
                            <Td
                              className={styles.NotesContainer}
                              data-label="הערות"
                            >
                              <div className={styles.NotesContent}>
                                {inspection.notes || "-"}
                              </div>
                            </Td>
                            <Td
                              className={styles.InspectorNameContainer}
                              data-label="שם הבודק"
                            >
                              {inspection.inspectorName}
                            </Td>
                            <Td
                              className={styles.RequesterNameContainer}
                              data-label="שם המזמין"
                            >
                              {inspection.requesterName}
                            </Td>
                            {!isInspection && (
                              <Td
                                className={styles.RemoveContainer}
                                data-label="מחק"
                              >
                                <div
                                  onClick={() =>
                                    showDeleteConfirm(
                                      inspection._id,
                                      inspection.carNumber,
                                      inspection.date
                                    )
                                  }
                                  className={styles.RowDeleteButton}
                                >
                                  <div className={styles.Delicon}>
                                    <DeleteIcon />
                                  </div>
                                </div>
                              </Td>
                            )}
                          </Tr>
                        ))}
                        {loading && hasMore && (
                          <Tr>
                            <Td
                              colSpan={colCount}
                              className={styles.LoadingMore}
                            >
                              טוען עוד...
                            </Td>
                          </Tr>
                        )}
                      </Tbody>
                    </Table>
                  </div>
                </div>
                {inspections.length > 10 && hasMore && (
                  <div ref={loadMoreRef} className={styles.LoadMoreTrigger} />
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CarInspectionTable;
